// ============================================================
// JsonImportDialog.tsx
// ------------------------------------------------------------
// Engångs-import-dialog för att läsa firestore-input-*.json
// (genererad av scripts/scan-dropbox-to-firestore-input.ps1)
// och pusha posterna till Firestore via importPosts().
//
// Flöde:
//   1) Användaren klickar "Välj JSON-fil"
//   2) FileReader läser filen, JSON.parse() → DropboxScanPost[]
//   3) Schema-validering + sammanfattning per bolag/kategori
//   4) "Importera N poster" → importPosts(...) med live progress
//   5) Slutresultat: X tillagda, Y skipped (dubletter), Z fel
//
// SVENSKA UI-text genomgående. Använder befintlig modal-stil
// (samma overlay/modal-pattern som DuplicateComparePopup).
// ============================================================

import { useEffect, useRef, useState } from 'react'
import { auth } from '../firebase'
import { importPosts, type DropboxScanPost, type ImportResult } from '../lib/dropboxImporter'

const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

interface Props {
  open: boolean
  onClose: () => void
}

interface CategoryEntry {
  count: number
  sumByCurrency: Record<string, number>
}

interface CompanyEntry {
  name: string
  count: number
  sumByCurrency: Record<string, number>
  byCategory: Map<string, CategoryEntry>
}

interface PreviewStats {
  total: number
  byCompany: Map<string, CompanyEntry>
  dateMin: string | null
  dateMax: string | null
  needsReview: number
}

type Phase = 'idle' | 'parsing' | 'preview' | 'importing' | 'done' | 'error'

// FIX (2026-05-23): visa summa per valuta i preview istället för att klumpa ihop
//   allt under "SEK". Tidigare räknades poster utan currency tyst som SEK
//   (`(p.currency ?? 'SEK') === 'SEK'`) — det dolde USD/EUR-poster och bröt totaler.
function formatSumByCurrency(sums: Record<string, number>): string {
  const entries = Object.entries(sums)
  if (entries.length === 0) return '0 SEK'
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cur, sum]) => fmt(sum) + ' ' + cur)
    .join(', ')
}

function buildPreview(posts: DropboxScanPost[]): PreviewStats {
  const byCompany = new Map<string, CompanyEntry>()
  let dateMin: string | null = null
  let dateMax: string | null = null
  let needsReview = 0

  for (const p of posts) {
    const cid = p.companyId || '(okänt)'
    let entry = byCompany.get(cid)
    if (!entry) {
      entry = { name: p.companyName || cid, count: 0, sumByCurrency: {}, byCategory: new Map() }
      byCompany.set(cid, entry)
    }
    entry.count++
    const cat = p.category || '(okänd)'
    let catEntry = entry.byCategory.get(cat)
    if (!catEntry) {
      catEntry = { count: 0, sumByCurrency: {} }
      entry.byCategory.set(cat, catEntry)
    }
    catEntry.count++
    if (typeof p.amount === 'number') {
      const cur = p.currency || 'UNKNOWN'  // UNKNOWN = saknas (currency null/empty)
      entry.sumByCurrency[cur] = (entry.sumByCurrency[cur] || 0) + p.amount
      catEntry.sumByCurrency[cur] = (catEntry.sumByCurrency[cur] || 0) + p.amount
    }

    if (p.date && /^\d{4}-\d{2}-\d{2}/.test(p.date)) {
      if (!dateMin || p.date < dateMin) dateMin = p.date
      if (!dateMax || p.date > dateMax) dateMax = p.date
    }
    if (p.status === 'needs_review') needsReview++
  }

  return { total: posts.length, byCompany, dateMin, dateMax, needsReview }
}

function validatePosts(raw: unknown): { ok: true; posts: DropboxScanPost[] } | { ok: false; error: string } {
  if (!Array.isArray(raw)) {
    return { ok: false, error: 'JSON måste vara en array av poster' }
  }
  if (raw.length === 0) {
    return { ok: false, error: 'JSON-arrayen är tom — inga poster att importera' }
  }
  const validCats = new Set(['invoice_in', 'invoice_out', 'expense'])
  for (let i = 0; i < raw.length; i++) {
    const p = raw[i] as Record<string, unknown>
    if (!p || typeof p !== 'object') return { ok: false, error: `Post #${i + 1}: är inte ett objekt` }
    if (typeof p.companyId !== 'string' || !p.companyId) return { ok: false, error: `Post #${i + 1}: saknar companyId` }
    if (typeof p.filename !== 'string' || !p.filename) return { ok: false, error: `Post #${i + 1}: saknar filename` }
    if (typeof p.category !== 'string' || !validCats.has(p.category)) {
      return { ok: false, error: `Post #${i + 1}: ogiltig category "${p.category}" (förväntas invoice_in/invoice_out/expense)` }
    }
  }
  return { ok: true, posts: raw as DropboxScanPost[] }
}

const CAT_LABEL: Record<string, string> = {
  invoice_in:  'Kundfakturor (Faktura IN)',
  invoice_out: 'Leverantörsfakturor (Faktura UT)',
  expense:     'Utlägg',
}

export function JsonImportDialog({ open, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [posts, setPosts] = useState<DropboxScanPost[] | null>(null)
  const [preview, setPreview] = useState<PreviewStats | null>(null)
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Stäng med Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'importing') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose, phase])

  // Reset state när dialogen stängs/öppnas
  useEffect(() => {
    if (!open) {
      setPhase('idle')
      setError(null)
      setFileName(null)
      setPosts(null)
      setPreview(null)
      setProgress(null)
      setResult(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [open])

  function handleFileSelected(file: File) {
    setPhase('parsing')
    setError(null)
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '')
        const raw = JSON.parse(text) as unknown
        const valid = validatePosts(raw)
        if (!valid.ok) {
          setError(valid.error)
          setPhase('error')
          return
        }
        setPosts(valid.posts)
        setPreview(buildPreview(valid.posts))
        setPhase('preview')
      } catch (e) {
        setError('Kunde inte tolka JSON: ' + (e as Error).message)
        setPhase('error')
      }
    }
    reader.onerror = () => {
      setError('Kunde inte läsa fil: ' + (reader.error?.message ?? 'okänt fel'))
      setPhase('error')
    }
    reader.readAsText(file)
  }

  async function runImport() {
    if (!posts) return
    const uid = auth.currentUser?.uid
    if (!uid) {
      setError('Du är inte inloggad — kan inte importera')
      setPhase('error')
      return
    }
    setPhase('importing')
    setProgress({ current: 0, total: posts.length })

    // För live-progress kör vi importPosts i chunks om 10 åt gången
    const CHUNK = 10
    const aggregated: ImportResult = { ok: 0, skipped: 0, failed: 0, errors: [] }
    try {
      for (let i = 0; i < posts.length; i += CHUNK) {
        const slice = posts.slice(i, i + CHUNK)
        const res = await importPosts(slice, uid, { dryRun: false })
        aggregated.ok += res.ok
        aggregated.skipped += res.skipped
        aggregated.failed += res.failed
        aggregated.errors.push(...res.errors)
        setProgress({ current: Math.min(i + CHUNK, posts.length), total: posts.length })
      }
      setResult(aggregated)
      setPhase('done')
    } catch (e) {
      setError('Importfel: ' + (e as Error).message)
      setPhase('error')
    }
  }

  if (!open) return null

  return (
    <div
      onClick={() => { if (phase !== 'importing') onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '2rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '1.25rem 1.5rem',
          width: 'min(720px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          color: 'var(--text)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>Importera Dropbox-JSON</div>
          <button
            onClick={onClose}
            disabled={phase === 'importing'}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.3rem', cursor: phase === 'importing' ? 'not-allowed' : 'pointer',
              padding: '0.25rem', opacity: phase === 'importing' ? 0.3 : 1,
            }}
          >{'✕'}</button>
        </div>

        {(phase === 'idle' || phase === 'parsing' || phase === 'error') && (
          <div>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Välj en JSON-fil genererad av Dropbox-skannern
              (<code>scripts/firestore-input-*.json</code>). Filen läses lokalt i din
              webbläsare — ingenting skickas förrän du klickat "Importera".
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={(e) => e.target.files?.[0] && handleFileSelected(e.target.files[0])}
              disabled={phase === 'parsing'}
            />
            {phase === 'parsing' && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                Tolkar {fileName}...
              </div>
            )}
            {phase === 'error' && error && (
              <div style={{
                marginTop: '0.75rem', padding: '0.6rem 0.8rem',
                background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
                borderRadius: 6, color: '#ef4444', fontSize: '0.9rem',
              }}>
                {'⚠ ' + error}
              </div>
            )}
          </div>
        )}

        {phase === 'preview' && preview && (
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              <b style={{ color: 'var(--text)' }}>{fileName}</b> &middot; {preview.total} poster
              {preview.dateMin && preview.dateMax && (
                <> &middot; {preview.dateMin} → {preview.dateMax}</>
              )}
              {preview.needsReview > 0 && (
                <> &middot; <span style={{ color: '#f59e0b' }}>{preview.needsReview} behöver granskning</span></>
              )}
            </div>

            <div style={{
              border: '1px solid var(--border)', borderRadius: 8,
              padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
            }}>
              {Array.from(preview.byCompany.entries()).map(([cid, info]) => (
                <div key={cid} style={{ padding: '0.5rem 0', borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.3rem' }}>
                    {info.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>({cid})</span>
                    <span style={{ float: 'right', color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>
                      {info.count} poster &middot; {formatSumByCurrency(info.sumByCurrency)}
                    </span>
                  </div>
                  {Array.from(info.byCategory.entries()).map(([cat, c]) => (
                    <div key={cat} style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.85rem', color: 'var(--text-muted)', padding: '0.15rem 0',
                    }}>
                      <span>&nbsp;&nbsp;&middot; {CAT_LABEL[cat] || cat}</span>
                      <span>{c.count} st &middot; {formatSumByCurrency(c.sumByCurrency)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div style={{
              background: 'rgba(59,130,246,0.08)', border: '1px solid #3b82f6',
              borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
              fontSize: '0.85rem', color: 'var(--text-muted)',
            }}>
              Importören gör en dubblett-check automatiskt mot
              <code> source='dropbox-scanner'</code> &mdash; redan importerade poster
              hoppas över (skipped).
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255,255,255,0.06)', color: 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  padding: '0.55rem 1rem', fontSize: '0.9rem', cursor: 'pointer',
                }}
              >Avbryt</button>
              <button
                onClick={runImport}
                style={{
                  background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '0.55rem 1rem',
                  fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
                }}
              >{'Importera ' + preview.total + ' poster'}</button>
            </div>
          </div>
        )}

        {phase === 'importing' && progress && (
          <div>
            <div style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>
              Importerar {progress.current} av {progress.total}...
            </div>
            <div style={{
              height: 8, background: 'rgba(255,255,255,0.08)',
              borderRadius: 4, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', width: ((progress.current / progress.total) * 100) + '%',
                background: '#10b981', transition: 'width 0.2s ease',
              }} />
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Stäng inte fliken — importen pågår.
            </div>
          </div>
        )}

        {phase === 'done' && result && (
          <div>
            <div style={{ fontSize: '0.95rem', marginBottom: '0.75rem' }}>
              {'✔️ Import klar'}
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem',
              marginBottom: '0.75rem',
            }}>
              <div style={{ padding: '0.6rem', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: 6 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Tillagda</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#10b981' }}>{result.ok}</div>
              </div>
              <div style={{ padding: '0.6rem', background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', borderRadius: 6 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Skipped (dubletter)</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#f59e0b' }}>{result.skipped}</div>
              </div>
              <div style={{ padding: '0.6rem', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 6 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Fel</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ef4444' }}>{result.failed}</div>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div style={{
                background: 'rgba(239,68,68,0.06)', border: '1px solid #ef4444',
                borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
                fontSize: '0.8rem', maxHeight: 200, overflowY: 'auto',
              }}>
                {result.errors.slice(0, 20).map((e, i) => (
                  <div key={i} style={{ padding: '0.15rem 0' }}>
                    <b>{e.filename}:</b> {e.error}
                  </div>
                ))}
                {result.errors.length > 20 && (
                  <div style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                    &hellip; och {result.errors.length - 20} till
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '0.55rem 1rem',
                  fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
                }}
              >Stäng</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default JsonImportDialog
